/**
 * 任务使用缓存形式存入db
 */

import { cloneDeep } from 'lodash';
import user from '@/renderer/scripts/utils/user';
import { TransUploadTask, TransDownloadTask, TaskStatus, TableName, TaskType, TaskInDB, TransSubTask } from './types';
import taskDB from './taskDB';

type AddParams = {
  task: TransUploadTask | TransDownloadTask;
  status: TaskStatus;
  type: TaskType;
};

type Cache = {
  uploadCache: Map<string, TransUploadTask>;
  downloadCache: Map<string, TransDownloadTask>;
  uploadUpdateTime: number;
  downloadUpdateTime: number;
  checkTimer: number | undefined;
  checkInterval: number;
  maxCacheLength: number;
};

/**
 * 所有任务更新先进入cache管理
 * 1、level0 大文件的成功 | 失败、所有文件的删除动作 立即执行更新
 * 2、level1 任务的其他状态更新 进入缓存 60s后统一执行
 * */
class TaskCache {
  private userId = -1;

  private orgId = -1;

  constructor() {
    this.initUserInfo();
  }

  public initUserInfo() {
    const { organizationId, userId } = user.getUserInfo();
    this.userId = userId as number;
    this.orgId = organizationId as number;
  }

  // 1级缓存 长度200 定时30s 缓存更久
  private level1Cache: Cache = {
    // 上传任务缓存
    uploadCache: new Map(),
    // 下载任务缓存
    downloadCache: new Map(),
    // 上传任务更新时间戳
    uploadUpdateTime: Date.now(),
    // 下载任务更新时间戳
    downloadUpdateTime: Date.now(),
    // 定时器
    checkTimer: undefined,
    // 定时轮询时间
    checkInterval: 30 * 1000,
    // 缓存最大长度 超过时立即更新db并释放缓存
    maxCacheLength: 200,
  };

  // 启动定时检查
  private startCheck() {
    // level1级缓存定时
    if (!this.level1Cache.checkTimer) {
      this.level1Cache.checkTimer = window.setInterval(() => {
        const levelCache = this.level1Cache;
        // 缓存已空 可以清掉定时器
        if (levelCache.uploadCache.size === 0 && levelCache.downloadCache.size === 0) {
          window.clearInterval(this.level1Cache.checkTimer);
          this.level1Cache.checkTimer = undefined;
          return;
        }
        const now = Date.now();
        const uploadTimeUp = now - levelCache.uploadUpdateTime > levelCache.checkInterval;
        const downloadTimeUp = now - levelCache.downloadUpdateTime > levelCache.checkInterval;
        const canUpdateUploadCache = uploadTimeUp && levelCache.uploadCache.size > 0;
        const canUpdateDownloadCache = downloadTimeUp && levelCache.downloadCache.size > 0;
        if (canUpdateUploadCache) {
          logger.ui(
            `[transmissionDBManager] >>***定时器检测到${levelCache.uploadCache.size}条上传任务待更新，触发更新`,
          );
          this.updateCache({ type: TaskType.upload });
        }
        if (canUpdateDownloadCache) {
          logger.ui(
            `[transmissionDBManager] >>***定时器检测到${levelCache.downloadCache.size}条下载任务待更新，触发更新`,
          );
          this.updateCache({ type: TaskType.download });
        }
      }, this.level1Cache.checkInterval);
    }
  }

  // 格式化要存入 db 的 task对象
  private formatTask(task: TransUploadTask | TransDownloadTask) {
    // clone task的指定参数
    const cloneTask: any = {};
    const ignoreKeys = ['getList', 'getListLength', 'getItem', 'sortListByStatus'];
    Object.keys(task).forEach(key => {
      if (!ignoreKeys.includes(key)) {
        cloneTask[key] = (task as any)[key];
      }
    });
    // clone 一份 taskList
    const taskList = task?.getList() || [];
    const cloneTaskList = cloneDeep(taskList);
    if (cloneTaskList.length > 0) {
      cloneTaskList.forEach((item: TransSubTask) => {
        // 必须删掉某些很深的且不需要的对象 否则存db会报错
        if (item.ins) {
          delete item.ins;
        }
        if (item.error?.config) {
          delete item.error.config;
        }
      });
    }
    cloneTask.list = cloneTaskList;
    return cloneTask;
  }

  // 从缓存中取出要更新到db的数据结构
  private getTodoList(map: Map<string, any>) {
    const deleteList: string[] = []; // 批量删除只需要taskId列表
    const putList: TaskInDB[] = [];
    [...map.values()].forEach(task => {
      if (task.status === TaskStatus.canceled) {
        deleteList.push(task.taskId);
      } else {
        const cleanTask = this.formatTask(task);
        putList.push({
          taskId: cleanTask.taskId,
          userId: this.userId,
          orgId: this.orgId,
          taskInfo: cleanTask,
        });
      }
    });
    return {
      deleteList,
      putList,
    };
  }

  // 取出缓存更新到db
  private updateCache({ type }: { type: TaskType }) {
    const levelCache = this.level1Cache;
    const cache = type === TaskType.upload ? levelCache.uploadCache : levelCache.downloadCache;
    const { deleteList, putList } = this.getTodoList(cache);
    if (putList.length > 0) {
      this.bulkPutTask(type, putList);
    }
    if (deleteList.length > 0) {
      this.bulkDeleteTask(type, deleteList);
    }
    // 更新updateTime
    if (type === TaskType.upload) {
      levelCache.uploadUpdateTime = Date.now();
    } else {
      levelCache.downloadUpdateTime = Date.now();
    }
    // 不需等待db更新完成 直接删除缓存
    cache.clear();
  }

  // 判断缓存级别
  private getTaskLevel(task: TransUploadTask | TransDownloadTask) {
    const { status, size = 0, isDir } = task;
    const list = task?.getList();
    let level;
    // 大文件定义：单文件>50MB | 文件夹数量 > 20
    const isBigFile = (!isDir && size > 50 * 1024 * 1024) || (isDir && list.length > 20);
    const isBigFileFinished = [TaskStatus.success, TaskStatus.error].includes(status) && isBigFile;
    if (isBigFileFinished) {
      level = 0;
    } else {
      level = 1;
    }
    return level;
  }

  // 删除某个任务的缓存
  private deleteCacheById({ taskId, type }: { taskId: string; type: TaskType }) {
    if (type === TaskType.upload) {
      this.level1Cache.uploadCache.delete(taskId);
    } else {
      this.level1Cache.downloadCache.delete(taskId);
    }
  }

  // 清空所有缓存并清理定时器
  public clear() {
    window.clearInterval(this.level1Cache.checkTimer);
    this.level1Cache.checkTimer = undefined;
    this.level1Cache.uploadCache.clear();
    this.level1Cache.downloadCache.clear();
  }

  // 所有任务更新都先走add
  public add({ task, type }: AddParams) {
    this.startCheck();
    const { taskId } = task;
    const level = this.getTaskLevel(task);
    if (level === 0) {
      // 立即更新并删除对应taskId的缓存 level0只有put没有delete
      this.putTask({ task, type });
      this.deleteCacheById({ taskId, type });
    } else if (level === 1) {
      // level1Cache更新
      const cache = type === TaskType.upload ? this.level1Cache.uploadCache : this.level1Cache.downloadCache;
      // @ts-ignore
      cache.set(taskId, task);
      // 达到缓存长度最大值 立即更新
      if (cache.size >= this.level1Cache.maxCacheLength) {
        logger.ui(
          `[transmissionDBManager] >>***检测到${cache.size}条${
            type === TaskType.upload ? '上传' : '下载'
          }任务达到缓存极限，触发更新`,
        );
        this.updateCache({ type });
      }
    }
  }

  // 立即更新全部缓存
  public updateCacheImmediately() {
    const level1UploadTasks = this.level1Cache.uploadCache.size;
    const level1ownloadTasks = this.level1Cache.downloadCache.size;
    if (level1UploadTasks > 0) {
      logger.ui(`[transmissionDBManager] >>***手动触发立即更新level1 ${level1UploadTasks}条上传记录`);
      this.updateCache({ type: TaskType.upload });
    }
    if (level1ownloadTasks > 0) {
      logger.ui(`[transmissionDBManager] >>***手动触发立即更新level1 ${level1ownloadTasks}条下载记录`);
      this.updateCache({ type: TaskType.download });
    }
  }

  // 单条更新到db
  private putTask({ task, type }: { task: TransUploadTask | TransDownloadTask; type: TaskType }) {
    const table = type === TaskType.upload ? TableName.uploadTable : TableName.downloadTable;
    const startTime = Date.now();
    const cleanTask = this.formatTask(task);
    taskDB
      .put(table, cleanTask.taskId, {
        taskId: cleanTask.taskId,
        userId: this.userId,
        orgId: this.orgId,
        taskInfo: cleanTask,
      })
      .then(() => {
        logger.ui(`[transmissionDBManager] >>***put执行成功，耗时${Date.now() - startTime}ms`);
      })
      .catch((e: any) => {
        logger.ui(`[transmissionDBManager] >>***put执行失败,原因：${e.message}`);
      });
  }

  // 批量更新到db
  private async bulkPutTask(type: TaskType, tasks: TaskInDB[]) {
    const table = type === TaskType.upload ? TableName.uploadTable : TableName.downloadTable;
    const startTime = Date.now();
    taskDB
      .bulkPut(table, tasks)
      .then(() => {
        logger.ui(
          `[transmissionDBManager] >>***bulkPut执行成功,执行${tasks.length}条，耗时${Date.now() - startTime}ms`,
        );
      })
      .catch((e: any) => {
        logger.ui(
          `[transmissionDBManager] >>***bulkPut执行失败,执行${tasks.length}条，失败${e?.failures?.length}条，原因：${e.message}`,
        );
      });
  }

  // 单条删除到db
  private deleteTask({ task, type }: { task: TransUploadTask | TransDownloadTask; type: TaskType }) {
    const table = type === TaskType.upload ? TableName.uploadTable : TableName.downloadTable;
    const startTime = Date.now();
    taskDB
      .delete(table, { taskId: task.taskId })
      .then(() => {
        logger.ui(`[transmissionDBManager] >>***delete执行成功，耗时${Date.now() - startTime}ms`);
      })
      .catch((e: any) => {
        logger.ui(`[transmissionDBManager] >>***delete执行失败，原因：${e.message}`);
      });
  }

  // 批量删除到db
  private async bulkDeleteTask(type: TaskType, tasks: string[]) {
    const table = type === TaskType.upload ? TableName.uploadTable : TableName.downloadTable;
    const startTime = Date.now();
    taskDB
      .bulkDelete(table, tasks)
      .then(() => {
        logger.ui(
          `[transmissionDBManager] >>***bulkDelete执行成功,执行${tasks.length}条，耗时${Date.now() - startTime}ms`,
        );
      })
      .catch((e: any) => {
        logger.ui(
          `[transmissionDBManager] >>***bulkDelete执行失败,执行${tasks.length}条，失败${e?.failures?.length}条，原因：${e.message}`,
        );
      });
  }
}

const taskCache = new TaskCache();
export default taskCache;
